import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import timeAgo from '../../../../../helpers/timeAgo';
import { StudentTeamsDto } from '../../../hooks/api/types';

interface IProps {
  assessment: any;
  studyGroupPk: string;
  displayUserPk?: string;
}

const useAssessmentContentRenderer = (props: IProps) => {
  const { assessment, displayUserPk, studyGroupPk } = props;
  const [isSaving, setIsSaving] = useState(false);
  const user = useContext(AuthContext);
  const isTeacher = user?.roles.includes(Role.teacher);
  const [studentTeams, setStudentTeams] = useState<any>([]);
  const [currentStudentTeam, setCurrentStudentTeam] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isContentChanged, setIsContentChanged] = useState(false);
  const [oldAnswers, setOldAnswers] = useState<any[]>([]);

  const assessmentId = assessment.id;
  const selectedDisplayUserPk = displayUserPk || user?.pk;

  const saveAll = async (answers: any, repeat = 1) => {
    setIsSaving(true);
    try {
      await Promise.all(
        Object.entries(answers).map(async ([questionId, answer]: any) => {
          await saveAssessmentAnswer({
            answer: answer.data || answer,
            questionId,
            studentTeamId: null,
          });
        }),
      );
    } catch (e) {
      if (repeat) {
        await saveAll(answers, repeat - 1);
      } else {
        console.log(e);
        alert('Ooops... Something went wrong. Try again!');
      }
    }
    setIsSaving(false);
  };

  const getAssessmentAnswers = async (studentTeamId: string | null) => {
    const response = await axios.get(
      `/api/v1/syllabus/assessment_answers/?assessment_id=${assessmentId}${
        studentTeamId
          ? `&student_team_id=${studentTeamId}`
          : `&user_id=${selectedDisplayUserPk}`
      }`,
    );
    return response.data;
  };

  const saveAssessmentAnswer = async ({
    answer,
    questionId,
    studentTeamId,
  }: {
    answer: string;
    questionId: number;
    studentTeamId: string | null;
  }) => {
    await axios.post(
      '/api/v1/syllabus/assessment_answers/',
      {
        answer,
        assessment_id: assessment.id,
        question_id: questionId,
        ...(studentTeamId
          ? { student_team_id: studentTeamId }
          : { user_id: selectedDisplayUserPk }),
      },
      {
        headers: {
          'X-CSRFToken': JSON.parse(
            document.getElementById('csrf_token')!.innerHTML,
          ),
        },
      },
    );
  };

  const getStudentTeams = async () => {
    const response = await axios.get<StudentTeamsDto[]>(
      `/api/v1/clients/student_teams/?study_group=${studyGroupPk}`,
    );
    setStudentTeams(response.data);
    return response.data;
  };

  const getStudentTeamId = async () => {
    const teams = await getStudentTeams();
    const team = teams.reduce((acc: any, team: any) => {
      if (team.fields.members.includes(selectedDisplayUserPk)) {
        return team;
      }
      return acc;
    }, null);
    const teamId = team?.pk || null;
    setCurrentStudentTeam(team || null);
    return teamId;
  };

  const initAssessments = async () => {
    setIsLoading(true);
    let studentTeamId = null;
    if (assessment.isGroup) {
      studentTeamId = await getStudentTeamId();
    }
    const answers = await getAssessmentAnswers(studentTeamId);
    setOldAnswers(answers);
    setIsLoading(false);
  };

  useEffect(() => {
    getStudentTeams();
    initAssessments();
  }, [selectedDisplayUserPk]);

  return {
    studentTeams,
    oldAnswers,
    currentStudentTeam,
    isLoading,
    isContentChanged,
    isSaving,
    isTeacher,
    saveAll,
    initAssessments,
  };
};

export default useAssessmentContentRenderer;
